import { useState } from "react";
import {
  Button,
  DatePicker,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Input,
  toast,
} from "@/components/kit";
import { formatDateForInput } from "@/shared/lib/date-time";
import { useCreateEventSale } from "../api/queries";
import { TouchField, touchControlClass } from "./touch-fields";

function keepCalendarInside(event: {
  target: EventTarget | null;
  preventDefault: () => void;
}) {
  if (
    event.target instanceof Element &&
    event.target.closest("[data-radix-popper-content-wrapper]")
  ) {
    event.preventDefault();
  }
}

type CreateEventDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function CreateEventDialog({
  open,
  onOpenChange,
}: CreateEventDialogProps) {
  const create = useCreateEventSale();
  const [name, setName] = useState("");
  const [place, setPlace] = useState("");
  const [startsOn, setStartsOn] = useState(() =>
    formatDateForInput(new Date()),
  );
  const [endsOn, setEndsOn] = useState("");
  const [note, setNote] = useState("");

  function reset() {
    setName("");
    setPlace("");
    setStartsOn(formatDateForInput(new Date()));
    setEndsOn("");
    setNote("");
  }

  async function submit() {
    if (!name.trim() || !startsOn) return;
    if (endsOn && endsOn < startsOn) {
      toast.error("ວັນສິ້ນສຸດຕ້ອງບໍ່ກ່ອນວັນເລີ່ມ");
      return;
    }
    await create.mutateAsync({
      name: name.trim(),
      place: place.trim() || null,
      startsOn,
      endsOn: endsOn || null,
      note: note.trim() || null,
    });
    toast.success("ສ້າງງານແລ້ວ");
    reset();
    onOpenChange(false);
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DrawerContent
        onInteractOutside={keepCalendarInside}
        onPointerDownOutside={keepCalendarInside}
      >
        <DrawerHeader>
          <DrawerTitle className="font-display text-xl">ສ້າງງານ</DrawerTitle>
        </DrawerHeader>
        <div className="flex flex-col gap-3 overflow-y-auto px-4">
          <TouchField label="ຊື່ງານ">
            <Input
              className={touchControlClass}
              value={name}
              placeholder="ຊື່ງານ"
              onChange={(event) => setName(event.target.value)}
            />
          </TouchField>
          <TouchField label="ສະຖານທີ່">
            <Input
              className={touchControlClass}
              value={place}
              placeholder="ບໍ່ບັງຄັບ"
              onChange={(event) => setPlace(event.target.value)}
            />
          </TouchField>
          <TouchField label="ວັນເລີ່ມ">
            <DatePicker
              className="h-12 w-full text-base"
              value={startsOn ? new Date(startsOn) : null}
              captionLayout="dropdown"
              fromYear={2024}
              toYear={new Date().getFullYear() + 2}
              onChange={(date) =>
                setStartsOn(date ? formatDateForInput(date) : "")
              }
            />
          </TouchField>
          <TouchField label="ວັນສິ້ນສຸດ">
            <DatePicker
              className="h-12 w-full text-base"
              placeholder="ບໍ່ບັງຄັບ"
              value={endsOn ? new Date(endsOn) : null}
              captionLayout="dropdown"
              fromYear={2024}
              toYear={new Date().getFullYear() + 2}
              onChange={(date) =>
                setEndsOn(date ? formatDateForInput(date) : "")
              }
            />
          </TouchField>
          <TouchField label="ໝາຍເຫດ">
            <Input
              className={touchControlClass}
              value={note}
              placeholder="ບໍ່ບັງຄັບ"
              onChange={(event) => setNote(event.target.value)}
            />
          </TouchField>
        </div>
        <DrawerFooter>
          <Button
            type="button"
            className="h-12 w-full text-base"
            disabled={!name.trim() || create.isPending}
            onClick={() => void submit()}
          >
            ບັນທຶກ
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
